'use client';

import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';

export function LeaderboardSearch({
  searchQuery,
  onSearchChange,
}: {
  searchQuery: string;
  onSearchChange: (query: string) => void;
}) {
  const t = useTranslations('leaderboard');
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (expanded) {
      const frame = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(frame);
    }
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        if (!searchQuery) setExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expanded, searchQuery]);

  const handleClose = () => {
    setExpanded(false);
    onSearchChange('');
  };

  return (
    <div ref={containerRef} className="relative flex items-center">
      <div
        className={cn(
          'flex items-center rounded-lg border transition-all duration-300 ease-out',
          expanded
            ? 'w-52 md:w-64 bg-white border-stone-300 shadow-sm'
            : 'w-9 h-9 border-transparent hover:bg-stone-100 cursor-pointer',
        )}
        onClick={() => !expanded && setExpanded(true)}
        role={expanded ? undefined : 'button'}
        tabIndex={expanded ? undefined : 0}
        onKeyDown={(e) => {
          if (!expanded && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setExpanded(true);
          }
        }}
      >
        <div
          className={cn(
            'flex-shrink-0 flex items-center justify-center',
            expanded ? 'w-9 h-9' : 'w-9 h-9',
          )}
        >
          <Search className="w-4 h-4 text-stone-500" />
        </div>

        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('searchPlaceholder')}
          aria-label={t('searchPlaceholder')}
          className={cn(
            'h-9 bg-transparent text-sm text-stone-900 placeholder:text-stone-400 outline-none transition-all duration-300',
            expanded ? 'w-full opacity-100 pr-1' : 'w-0 opacity-0 pointer-events-none',
          )}
          tabIndex={expanded ? 0 : -1}
          onKeyDown={(e) => {
            if (e.key === 'Escape') handleClose();
          }}
        />

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          className={cn(
            'flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full hover:bg-stone-100 mr-1 transition-all duration-200',
            expanded && searchQuery
              ? 'opacity-100 scale-100'
              : 'opacity-0 scale-75 pointer-events-none w-0 mr-0',
          )}
          tabIndex={expanded && searchQuery ? 0 : -1}
          aria-label="Clear search"
        >
          <X className="w-3.5 h-3.5 text-stone-400" />
        </button>
      </div>
    </div>
  );
}
